import type { PlayerProfile } from "../stats/profile";

/**
 * Seasonal events: a cosmetic given away for turning up during a window, rather
 * than earned by a level, earned by an achievement, or bought.
 *
 * The rule a player meets is two conditions and a date: SIGNED IN WITH GOOGLE,
 * and COMPLETED A ROUND, inside the window. Taking part is written into the
 * profile (`profile.events`, id -> when), and that record — not the calendar —
 * is what unlocks the prize from then on. So it is kept forever by the people
 * who were there, and closing the window takes nothing away from anybody.
 *
 * WHY SIGNED IN. The point of the giveaway is to reward an ACCOUNT, and the
 * profile only follows an account when it syncs (stats/cloudSync.ts), which only
 * happens for a real sign-in. An anonymous round in October would unlock the
 * set on one browser and lose it with the next clear of site data.
 *
 * WHY A SEPARATE FIELD AND NOT AN ACHIEVEMENT. An achievement would have come
 * with the gift ceremony for free, and with three things that are wrong for a
 * once-only event: it pays XP, it counts towards the Progress screen's total —
 * which every player who joins after the window could then never complete — and
 * it would sit in the achievement list as something nobody can earn any more.
 *
 * WINDOWS ARE IN THE PLAYER'S OWN ZONE. "September or October" is a question a
 * player answers off their own calendar, so the dates are built from local
 * components at load rather than pinned to UTC; October ends at midnight where
 * they are, not at 5pm the day before for somebody in California.
 *
 * The clock is the device's. Like every other unlock here, this is a
 * client-evaluated cosmetic and forging it only cheats its forger — see the
 * trust model in docs/CLOUD_SAVES_DESIGN.md §2.
 */
export type CosmeticEvent = OnceEvent | YearlyEvent;

/** One window, and then never again. */
export interface OnceEvent {
  id: string;
  /** Epoch ms the window opens, inclusive. */
  opensMs: number;
  /** Epoch ms the window closes, exclusive. */
  closesMs: number;
  /** The last day of the window as a player reads it, for the requirement line. */
  lastDay: string;
}

/** The same calendar month every year, so it never closes for good. */
export interface YearlyEvent {
  id: string;
  /** The month it runs, as `Date.getMonth` counts them: December is 11. */
  month: number;
  /** That month as a player reads it, for the requirement line. */
  monthName: string;
}

export const EVENTS: CosmeticEvent[] = [
  {
    // The Midnight Macabre set (theme/sets.ts): any round, September or
    // October 2026.
    id: "halloween-2026",
    opensMs: new Date(2026, 8, 1).getTime(),
    closesMs: new Date(2026, 10, 1).getTime(),
    lastDay: "Oct 31",
  },
  {
    // The Yuletide Joy deck (theme/unlocks.ts): any round, in any December.
    id: "yuletide",
    month: 11,
    monthName: "December",
  },
];

export function eventById(id: string): CosmeticEvent | undefined {
  return EVENTS.find((e) => e.id === id);
}

/** Whether a round played at this moment counts. Local components, per the note above. */
function isEventOpen(event: CosmeticEvent, nowMs: number): boolean {
  return "month" in event ? new Date(nowMs).getMonth() === event.month : nowMs >= event.opensMs && nowMs < event.closesMs;
}

/**
 * True when the event's prize should be off the shelves for anyone without it: a
 * yearly event in every month but its own, a one-off once its window has passed,
 * and always an id this build does not know.
 */
export function hasEventClosed(id: string, nowMs: number): boolean {
  const event = eventById(id);
  if (!event) return true;
  return "month" in event ? !isEventOpen(event, nowMs) : nowMs >= event.closesMs;
}

// ---------------------------------------------------------------------------
// Who is signed in
// ---------------------------------------------------------------------------

/**
 * Whether the current session is a Google account, fed by the account store's
 * auth listener (`initAccountStore`).
 *
 * A module variable rather than a read of `useAccountStore`, for the reason
 * `entitlements.ts` keeps its host comp in one: the round recorder asks this,
 * and the account store builds a Firebase client the moment it is evaluated.
 * A static import would put the network stack at the root of the recorder, and
 * of every test that records a round.
 */
let googleAccount = false;

export function setSignedInWithGoogle(value: boolean): void {
  googleAccount = value;
}

export function isSignedInWithGoogle(): boolean {
  return googleAccount;
}

/**
 * Records this round against every event whose window it falls in. Mutates, so
 * call it inside `updateProfile`; returns the ids it newly wrote.
 *
 * The first round is the one kept — a later one changes nothing — which is the
 * same "earliest is what happened" rule `mergeProfiles` applies to the field.
 */
export function recordEventParticipation(
  profile: PlayerProfile,
  nowMs: number,
  signedInWithGoogle: boolean = googleAccount,
): string[] {
  if (!signedInWithGoogle) return [];
  const earned: string[] = [];
  for (const event of EVENTS) {
    if (profile.events[event.id]) continue;
    if (isEventOpen(event, nowMs)) {
      profile.events[event.id] = nowMs;
      earned.push(event.id);
    }
  }
  return earned;
}
